import { useClerk } from '@clerk/react-router';
import { useState } from 'react';
import { useConfirmationView } from '~/app';
import { Button } from '~/components/buttons/Button/Button';
import { paths } from '~/routing/routes';
import { postData } from '~/utils/service';
import {
  SETTINGS_ACTION_ROW_CLASS,
  SETTINGS_CONTROLS_CLASS,
  SETTINGS_INTRO_CLASS,
  SETTINGS_SECTION_CLASS,
} from './settingsLayout';

export const AccountDeactivationSection = () => {
  const [deactivating, setDeactivating] = useState(false);
  const [deactivationError, setDeactivationError] = useState<string | null>(null);
  const { signOut } = useClerk();
  const confirmationRef = useConfirmationView();

  const performAccountDeactivation = async () => {
    setDeactivationError(null);
    setDeactivating(true);

    try {
      await postData('userInfoDeactivate');
    } catch {
      setDeactivationError('Could not deactivate your account. Please try again.');
      setDeactivating(false);
      return;
    }

    // Clerk's ban revokes active sessions, so signOut may observe an already
    // invalid session. Either way, leave the authenticated shell only after
    // the server has confirmed that deactivation succeeded.
    try {
      await signOut();
    } catch {
      // The account is already banned. The hard navigation clears app state.
    }
    window.location.assign(paths.landing);
  };

  const handleDeactivateAccountClick = () => {
    confirmationRef?.send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Deactivate account',
        message:
          'Deactivate your account and sign out? You will not be able to sign in again unless your account is reactivated. Your account data will be retained.',
        confirmLabel: 'Deactivate account',
        onConfirm: performAccountDeactivation,
      },
    });
  };

  return (
    <section className={SETTINGS_SECTION_CLASS}>
      <div className={SETTINGS_INTRO_CLASS}>
        <h2 className="text-base font-semibold text-grey-100">Deactivate account</h2>
        <p className="text-sm text-grey-400">
          Disable your sign-in and end your current session. Your account data will be retained. An
          administrator must reactivate the account before you can sign in again.
        </p>
      </div>
      <div className={SETTINGS_CONTROLS_CLASS}>
        {deactivationError && (
          <p className="text-sm text-danger" role="alert">
            {deactivationError}
          </p>
        )}
        <div className={SETTINGS_ACTION_ROW_CLASS}>
          <Button
            label={deactivating ? 'Deactivating…' : 'Deactivate account'}
            type="button"
            colorVariant="error"
            disabled={deactivating}
            onClick={handleDeactivateAccountClick}
          />
        </div>
      </div>
    </section>
  );
};
